import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  BoxProps
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  CallReceived as ReceiveIcon,
  CallMade as SendIcon,
  Token as TokenIcon,
  Image as NFTIcon,
  NavigateNext as NextIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';
import { styled as muiStyled } from '@mui/material/styles';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatAmount, formatBlockNumber } from '../../utils/format';

// Token details configuration
interface TokenDetails {
  symbol: string;
  name: string;
  imageUrl: string;
}

const tokenDetailsMap: { [key: string]: TokenDetails } = {
  '8c77de1427156b98fb15cce77d908f79bd69f6b4b8e3a60d8e051dac481b5365': {
    symbol: 'STR',
    name: 'StarBits',
    imageUrl: 'https://xch.live/str.webp'
  },
  // Add more tokens as needed
};

interface StyledProps {
  isPositive: boolean;
}

interface MainContentProps {
  hasCoins?: boolean;
}

const EventCard = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  borderRadius: '12px',
  marginBottom: '16px'
}));

const TokenDisplay = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const TokenInfo = muiStyled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const StyledTokenIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: 12px;
`;

const MetaInfo = muiStyled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '4px',
  color: '#6B7280',
  fontSize: '13px'
});

const DetailsSection = styled(Box)`
  margin-top: 8px;
`;

const DetailsSummary = styled(Box)`
  color: #4B5563;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

const CoinsContainer = styled(Box)`
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BlockLink = muiStyled(Link)({
  color: '#2563EB',
  textDecoration: 'none',
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline'
  }
});

const MainContent = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasCoins',
})<MainContentProps>(({ hasCoins }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: hasCoins ? '16px' : '0'
}));

const TransactionInfo = muiStyled(Box)({
  flex: 1
});

const StyledTransactionAmount = muiStyled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isPositive',
})<StyledProps>(({ theme, isPositive }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: isPositive ? '#166534' : '#991B1B',
  display: 'flex',
  alignItems: 'center'
}));

const StyledCoinsGrid = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPositive',
})<StyledProps>(({ theme, isPositive }) => ({
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: `1px solid ${isPositive ? '#DCFCE7' : '#FEE2E2'}`
}));

const CompactCoinLink = muiStyled(Link)({
  display: 'block',
  fontFamily: 'monospace',
  textDecoration: 'none',
  color: '#2563EB',
  marginBottom: '4px',
  '&:hover': {
    textDecoration: 'underline'
  }
});

const SideContent = styled(Box)`
  width: 280px;
  border-left: 1px solid ${({ theme }) => theme.palette.divider};
  padding-left: 24px;
`;

const TimelineContainer = styled(Box)`
  margin-top: 2rem;
`;

const AddressCard = styled(Paper)`
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.palette.background.paper};
`;

const AddressText = styled(Typography)`
  font-family: monospace;
  word-break: break-all;
`;

const LoadMoreButton = styled(Button)`
  margin-top: 1rem;
  width: 100%;
`;

const CoinDetailsButton = styled(Button)`
  text-transform: none;
  font-size: 0.75rem;
  padding: 2px 8px;
  min-width: 0;
  margin-left: 8px;
  height: 20px;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 1rem;
  border-radius: 8px;
`;

const TotalAddressesChip = styled(Chip)`
  margin: 1rem 0;
`;

const CopyButton = styled(IconButton)`
  padding: 4px;
`;

const SubText = styled(Typography)`
  color: #6B7280;
  font-size: 14px;
`;

const DetailSection = styled(Box)<{ $type: 'receive' | 'send' }>`
  font-size: 13px;
  color: #6B7280;
  border-top: 1px solid ${({ $type }) => 
    $type === 'receive' ? '#DCFCE7' : '#FEE2E2'};
  padding-top: 12px;
  margin-top: 12px;
`;

interface CoinsSectionProps {
  isPositive: boolean;
}

const CoinsSection = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPositive',
})<CoinsSectionProps>(({ isPositive }) => ({
  borderTop: `1px solid ${isPositive ? '#DCFCE7' : '#FEE2E2'}`,
  paddingTop: '12px',
  marginTop: '12px'
}));

const CoinsList = muiStyled(Box)({
  paddingLeft: '16px',
  marginTop: '8px'
});

const CoinLink = muiStyled(Link)({
  color: '#2563EB',
  textDecoration: 'none',
  display: 'block',
  marginBottom: '4px',
  fontFamily: 'monospace',
  fontSize: '13px',
  '&:hover': {
    textDecoration: 'underline'
  }
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface Event {
  id: string;
  walletId: number;
  timestamp: number;
  blockHeight: number;
  blockTime: number;
  data: {
    xch?: {
      aggregatedAmount: number;
      receivedCoins: string[];
      spentCoins: string[];
    };
    tokens?: Array<{
      aggregatedAmount: number;
      tokenId: string;
      receivedCoins: string[];
      spentCoins: string[];
    }>;
    nfts?: {
      sent: string[];
      received: string[];
    };
  };
}

interface Address {
  address: string;
  index: number;
}

const Dashboard: React.FC = () => {
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [nextKey, setNextKey] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const { t } = useLanguage();

  // Get walletId from location state
  const walletId = location.state?.walletId;

  useEffect(() => {
    if (walletId) {
      fetchEvents();
      fetchAddresses();
    }
  }, [walletId]);

  // Replace the fetchEvents function with mock data for testing
  const fetchEvents = async (key?: string) => {
    try {
      setLoading(true);
      setError(null);
      const url = new URL(`${process.env.REACT_APP_API_BASE_URL}/wallet/events`);
      url.searchParams.append('walletId', walletId.toString());
      if (key) {
        url.searchParams.append('nextKey', key);
      }

      const response = await fetch(url.toString());
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch events');
      }

      setEvents(prev => key ? [...prev, ...data.events] : data.events);
      setNextKey(data.pagination.nextKey);
      setHasMore(data.pagination.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/get-addresses?walletId=${walletId}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch addresses');
      }

      setAddresses(data.addresses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLoadMore = () => {
    if (nextKey) {
      fetchEvents(nextKey);
    }
  };

  const toggleEventExpansion = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  // Update the renderEventContent function to handle all types
  const renderEventContent = (event: Event) => {
    const eventTime = (event.blockTime || event.timestamp) * 1000;
    const timeAgo = formatDistanceToNow(eventTime, { addSuffix: true });
    
    // XCH Transaction
    if (event.data.xch) {
      const { aggregatedAmount, receivedCoins = [], spentCoins = [] } = event.data.xch;
      const isPositive = aggregatedAmount >= 0;
      const hasCoins = receivedCoins.length > 0 || spentCoins.length > 0;
      const isExpanded = expandedEvents.has(event.id);

      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '16px',
          background: (theme) => theme.palette.mode === 'dark' 
            ? (isPositive ? 'rgba(22, 101, 52, 0.1)' : 'rgba(153, 27, 27, 0.1)')
            : (isPositive ? '#F0FDF4' : '#FEF2F2'),
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 1px 3px rgba(0, 0, 0, 0.3)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: (theme) => `1px solid ${
            theme.palette.mode === 'dark'
              ? (isPositive ? 'rgba(22, 101, 52, 0.5)' : 'rgba(153, 27, 27, 0.5)')
              : (isPositive ? '#DCFCE7' : '#FEE2E2')
          }`
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: hasCoins && isExpanded ? '12px' : '0'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <img
                src="/xch.webp"
                alt="XCH"
                style={{ width: 32, height: 32, borderRadius: '50%' }}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ 
                    color: (theme) => theme.palette.mode === 'dark'
                      ? (isPositive ? '#86EFAC' : '#FCA5A5')
                      : (isPositive ? '#166534' : '#991B1B'),
                    fontSize: '20px',
                    fontWeight: 600 
                  }}
                >
                  {isPositive ? '+' : '-'}{formatAmount(Math.abs(aggregatedAmount))} XCH
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '4px',
              color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
              fontSize: '13px'
            }}>
              <Link
                href={`https://www.spacescan.io/block/${event.blockHeight}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('dashboard.events.block')}: <a href={`https://www.spacescan.io/block/${event.blockHeight}`} style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 500 }}>{formatBlockNumber(event.blockHeight)}</a>
              </Link>
              <Typography>{timeAgo}</Typography>
            </Box>
          </Box>

          {hasCoins && (
            <>
              <Box 
                onClick={() => toggleEventExpansion(event.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  cursor: 'pointer',
                  userSelect: 'none',
                  mt: 1.5,
                  fontSize: '12px',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#D1D5DB' : '#4B5563'
                  }
                }}
              >
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: 'inherit' }}>
                  {receivedCoins.length > 0 && `${receivedCoins.length} ${t('dashboard.events.received')}`}
                  {receivedCoins.length > 0 && spentCoins.length > 0 && ' • '}
                  {spentCoins.length > 0 && `${spentCoins.length} ${t('dashboard.events.spent')}`}
                </Typography>
              </Box>

              {isExpanded && (
                <Box sx={{
                  borderTop: (theme) => `1px solid ${
                    theme.palette.mode === 'dark'
                      ? (isPositive ? 'rgba(22, 101, 52, 0.5)' : 'rgba(153, 27, 27, 0.5)')
                      : (isPositive ? '#DCFCE7' : '#FEE2E2')
                  }`,
                  paddingTop: '8px',
                  marginTop: '8px',
                  opacity: theme => theme.palette.mode === 'dark' ? 0.9 : 0.8
                }}>
                  {receivedCoins.length > 0 && (
                    <>
                      <Typography sx={{ 
                        color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px'
                      }}>
                        {t('dashboard.events.received')}
                      </Typography>
                      <Box sx={{ 
                        paddingLeft: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {receivedCoins.map((coin) => (
                          <Link
                            key={coin}
                            href={`https://www.spacescan.io/coin/${coin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                              textDecoration: 'none',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(37, 99, 235, 0.2)'
                                : 'rgba(37, 99, 235, 0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.3)'
                                  : 'rgba(37, 99, 235, 0.15)',
                                textDecoration: 'none'
                              }
                            }}
                          >
                            {coin.slice(-6)}
                          </Link>
                        ))}
                      </Box>
                    </>
                  )}
                  
                  {spentCoins.length > 0 && (
                    <Box sx={{ marginTop: receivedCoins.length > 0 ? '8px' : '0' }}>
                      <Typography sx={{ 
                        color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px'
                      }}>
                        {t('dashboard.events.spent')}
                      </Typography>
                      <Box sx={{ 
                        paddingLeft: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {spentCoins.map((coin) => (
                          <Link
                            key={coin}
                            href={`https://www.spacescan.io/coin/${coin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                              textDecoration: 'none',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(37, 99, 235, 0.2)'
                                : 'rgba(37, 99, 235, 0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.3)'
                                  : 'rgba(37, 99, 235, 0.15)',
                                textDecoration: 'none'
                              }
                            }}
                          >
                            {coin.slice(-6)}
                          </Link>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      );
    }

    // Token Transaction
    if (event.data.tokens && event.data.tokens.length > 0) {
      const token = event.data.tokens[0]; // Handle first token for now
      const isPositive = token.aggregatedAmount >= 0;
      const hasCoins = (token.receivedCoins?.length || 0) > 0 || (token.spentCoins?.length || 0) > 0;
      const isExpanded = expandedEvents.has(event.id);

      // Get token details from map or use fallback
      const tokenDetails = tokenDetailsMap[token.tokenId] || {
        symbol: token.tokenId.slice(0, 4),
        name: 'Unknown Token',
        imageUrl: '/unknown-token.webp'
      };

      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '16px',
          background: (theme) => theme.palette.mode === 'dark' 
            ? (isPositive ? 'rgba(22, 101, 52, 0.1)' : 'rgba(153, 27, 27, 0.1)')
            : (isPositive ? '#F0FDF4' : '#FEF2F2'),
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 1px 3px rgba(0, 0, 0, 0.3)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: (theme) => `1px solid ${
            theme.palette.mode === 'dark'
              ? (isPositive ? 'rgba(22, 101, 52, 0.5)' : 'rgba(153, 27, 27, 0.5)')
              : (isPositive ? '#DCFCE7' : '#FEE2E2')
          }`
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: hasCoins && isExpanded ? '12px' : '0'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <img
                src={tokenDetails.imageUrl}
                alt={tokenDetails.symbol}
                style={{ width: 32, height: 32, borderRadius: '50%', cursor: 'pointer' }}
                onClick={() => window.open(`https://www.spacescan.io/token/${tokenDetails.symbol}`, '_blank')}
              />
              <Box>
                <Typography
                  variant="h6"
                  sx={{ 
                    color: (theme) => theme.palette.mode === 'dark'
                      ? (isPositive ? '#86EFAC' : '#FCA5A5')
                      : (isPositive ? '#166534' : '#991B1B'),
                    fontSize: '20px',
                    fontWeight: 600 
                  }}
                >
                  {isPositive ? '+' : '-'}{formatAmount(Math.abs(token.aggregatedAmount))} {tokenDetails.symbol}
                </Typography>
                <Typography sx={{ 
                  color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  fontSize: '14px'
                }}>
                  {tokenDetails.name}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '4px',
              color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
              fontSize: '13px'
            }}>
              <Link
                href={`https://www.spacescan.io/block/${event.blockHeight}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('dashboard.events.block')}: <a href={`https://www.spacescan.io/block/${event.blockHeight}`} style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 500 }}>{formatBlockNumber(event.blockHeight)}</a>
              </Link>
              <Typography>{timeAgo}</Typography>
            </Box>
          </Box>

          {hasCoins && (
            <>
              <Box 
                onClick={() => toggleEventExpansion(event.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  cursor: 'pointer',
                  userSelect: 'none',
                  mt: 1.5,
                  fontSize: '12px',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#D1D5DB' : '#4B5563'
                  }
                }}
              >
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: 'inherit' }}>
                  {token.receivedCoins?.length > 0 && `${token.receivedCoins.length} ${t('dashboard.events.received')}`}
                  {token.receivedCoins?.length > 0 && token.spentCoins?.length > 0 && ' • '}
                  {token.spentCoins?.length > 0 && `${token.spentCoins.length} ${t('dashboard.events.spent')}`}
                </Typography>
              </Box>

              {isExpanded && (
                <Box sx={{
                  borderTop: (theme) => `1px solid ${
                    theme.palette.mode === 'dark'
                      ? (isPositive ? 'rgba(22, 101, 52, 0.5)' : 'rgba(153, 27, 27, 0.5)')
                      : (isPositive ? '#DCFCE7' : '#FEE2E2')
                  }`,
                  paddingTop: '8px',
                  marginTop: '8px',
                  opacity: theme => theme.palette.mode === 'dark' ? 0.9 : 0.8
                }}>
                  {token.receivedCoins?.length > 0 && (
                    <>
                      <Typography sx={{ 
                        color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px'
                      }}>
                        {t('dashboard.events.received')}
                      </Typography>
                      <Box sx={{ 
                        paddingLeft: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {token.receivedCoins.map((coin) => (
                          <Link
                            key={coin}
                            href={`https://www.spacescan.io/coin/${coin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                              textDecoration: 'none',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(37, 99, 235, 0.2)'
                                : 'rgba(37, 99, 235, 0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.3)'
                                  : 'rgba(37, 99, 235, 0.15)',
                                textDecoration: 'none'
                              }
                            }}
                          >
                            {coin.slice(-6)}
                          </Link>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      );
    }

    // NFT Transaction
    if (event.data.nfts) {
      const { received = [], sent = [] } = event.data.nfts;
      const isPositive = received.length > 0;
      const hasNFTs = received.length > 0 || sent.length > 0;
      const isExpanded = expandedEvents.has(event.id);

      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '16px',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(30, 64, 175, 0.1)'
            : '#F0F9FF',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 1px 3px rgba(0, 0, 0, 0.3)'
            : '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: (theme) => `1px solid ${
            theme.palette.mode === 'dark'
              ? 'rgba(30, 64, 175, 0.5)'
              : '#DBEAFE'
          }`
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: hasNFTs && isExpanded ? '12px' : '0'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(30, 64, 175, 0.3)'
                  : '#DBEAFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '16px' }}>🖼️</span>
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ 
                    color: (theme) => theme.palette.mode === 'dark'
                      ? '#93C5FD'
                      : '#1E40AF',
                    fontSize: '20px',
                    fontWeight: 600 
                  }}
                >
                  {received.length > 0 ? `${received.length} ${t('dashboard.events.nftsReceived')}` : `${sent.length} ${t('dashboard.events.nftsSent')}`}
                </Typography>
                <Typography sx={{ 
                  color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  fontSize: '14px'
                }}>
                  {t('dashboard.events.nftTransaction')}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '4px',
              color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
              fontSize: '13px'
            }}>
              <Link
                href={`https://www.spacescan.io/block/${event.blockHeight}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('dashboard.events.block')}: <a href={`https://www.spacescan.io/block/${event.blockHeight}`} style={{ color: '#2563EB', textDecoration: 'none', fontWeight: 500 }}>{formatBlockNumber(event.blockHeight)}</a>
              </Link>
              <Typography>{timeAgo}</Typography>
            </Box>
          </Box>

          {hasNFTs && (
            <>
              <Box 
                onClick={() => toggleEventExpansion(event.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: (theme) => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  cursor: 'pointer',
                  userSelect: 'none',
                  mt: 1.5,
                  fontSize: '12px',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#D1D5DB' : '#4B5563'
                  }
                }}
              >
                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: 'inherit' }}>
                  {t('dashboard.events.viewNFTDetails')}
                </Typography>
              </Box>

              {isExpanded && (
                <Box sx={{
                  borderTop: (theme) => `1px solid ${
                    theme.palette.mode === 'dark'
                      ? 'rgba(30, 64, 175, 0.5)'
                      : '#DBEAFE'
                  }`,
                  paddingTop: '8px',
                  marginTop: '8px',
                  opacity: theme => theme.palette.mode === 'dark' ? 0.9 : 0.8
                }}>
                  {received.length > 0 && (
                    <>
                      <Typography sx={{ 
                        color: (theme) => theme.palette.mode === 'dark'
                          ? '#93C5FD'
                          : '#1E40AF',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px'
                      }}>
                        {t('dashboard.events.receivedNFTs')}
                      </Typography>
                      <Box sx={{ 
                        paddingLeft: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {received.map((nft) => (
                          <Link
                            key={nft}
                            href={`https://www.spacescan.io/nft/${nft}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                              textDecoration: 'none',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(37, 99, 235, 0.2)'
                                : 'rgba(37, 99, 235, 0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.3)'
                                  : 'rgba(37, 99, 235, 0.15)',
                                textDecoration: 'none'
                              }
                            }}
                          >
                            {nft}
                          </Link>
                        ))}
                      </Box>
                    </>
                  )}

                  {sent.length > 0 && (
                    <Box sx={{ marginTop: received.length > 0 ? '8px' : '0' }}>
                      <Typography sx={{ 
                        color: (theme) => theme.palette.mode === 'dark'
                          ? '#93C5FD'
                          : '#1E40AF',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px'
                      }}>
                        {t('dashboard.events.sentNFTs')}
                      </Typography>
                      <Box sx={{ 
                        paddingLeft: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {sent.map((nft) => (
                          <Link
                            key={nft}
                            href={`https://www.spacescan.io/nft/${nft}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => theme.palette.mode === 'dark' ? '#60A5FA' : '#2563EB',
                              textDecoration: 'none',
                              fontSize: '11px',
                              fontFamily: 'monospace',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(37, 99, 235, 0.2)'
                                : 'rgba(37, 99, 235, 0.1)',
                              '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                  ? 'rgba(37, 99, 235, 0.3)'
                                  : 'rgba(37, 99, 235, 0.15)',
                                textDecoration: 'none'
                              }
                            }}
                          >
                            {nft}
                          </Link>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderAddressesTab = () => {
    if (loading && addresses.length === 0) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );
    }

    const sortedAddresses = [...addresses].sort((a, b) => a.index - b.index);

    return (
      <>
        <TotalAddressesChip
          label={t('dashboard.addresses.totalAddresses', { count: addresses.length })}
          color="primary"
          variant="outlined"
        />
        <Paper>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard.addresses.index')}</TableCell>
                  <TableCell>{t('dashboard.addresses.address')}</TableCell>
                  <TableCell align="right">{t('dashboard.addresses.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAddresses.map((addr) => (
                  <TableRow key={addr.index}>
                    <TableCell component="th" scope="row">
                      {addr.index}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        fontFamily: 'monospace', 
                        wordBreak: 'break-all'
                      }}>
                        {addr.address}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={copiedAddress === addr.address ? "Copied!" : "Copy Address"}>
                        <CopyButton
                          onClick={() => handleCopyAddress(addr.address)}
                          color={copiedAddress === addr.address ? "success" : "default"}
                        >
                          {copiedAddress === addr.address ? <CheckCircle /> : <ContentCopy />}
                        </CopyButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Paper>
      </>
    );
  };

  if (!walletId) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">No wallet selected</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          {t('dashboard.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t('dashboard.tabs.events')} />
            <Tab label={t('dashboard.tabs.addresses')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TimelineContainer>
            {events.map(event => renderEventContent(event))}
            
            {loading && (
              <Box display="flex" justifyContent="center" my={3}>
                <CircularProgress />
              </Box>
            )}

            {hasMore && !loading && (
              <LoadMoreButton
                variant="outlined"
                onClick={handleLoadMore}
                endIcon={<NextIcon />}
              >
                Load More Events
              </LoadMoreButton>
            )}

            {!loading && events.length === 0 && (
              <Alert severity="info">No events found</Alert>
            )}
          </TimelineContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderAddressesTab()}
        </TabPanel>
      </Box>
    </Container>
  );
};

export default Dashboard; 